/**
 * 하단 네비게이션
 *
 * 픽업/테이블 주문 모드와 일반 탭 네비게이션을 제공합니다.
 */
import { useLocation } from 'react-router-dom';
import { getTabFromPath } from '@/config/routes';
import { useLanguage } from '@/contexts/AppContext';
import { getBottomNavigationTranslations } from './constants';
import { type BottomNavigationProps } from './types';
import { PickupCartNav } from './PickupCartNav';
import { TabNav } from './TabNav';

export function BottomNavigation({
  activeTab: activeTabProp,
  onTabChange,
  showPickupMenu = false,
  cartTotal = "$650",
  cartItemCount = 3,
  onCartClick,
  mode = 'pickup',
  orderedItemCount = 0,
  onPaymentClick,
}: BottomNavigationProps) {
  const { locale } = useLanguage();
  const location = useLocation();
  const activeTab = activeTabProp ?? getTabFromPath(location.pathname);
  const t = getBottomNavigationTranslations(locale);

  // If showing pickup menu, render cart navigation
  if (showPickupMenu) {
    return (
      <PickupCartNav
        cartTotal={cartTotal}
        cartItemCount={cartItemCount}
        mode={mode}
        orderedItemCount={orderedItemCount}
        onCartClick={onCartClick}
        onPaymentClick={onPaymentClick}
        t={t}
      />
    );
  }

  // #648(codex Critical): route 기반 메뉴 페이지(/restaurant/*)에서는 TabNav 미렌더.
  // 매장이 근처 리스트에 없거나 테이블 토큰 빠른 경로(useRestaurants URL 파생)를 못 타면
  // showPickupMenu/showTableOrderMenu 가 false 인 채 RestaurantRoute → RestaurantPage 가 메뉴를
  // 렌더하는데, 이때 TabNav(fixed bottom-0, z-50)가 PickupMenu 하단 도크(sticky, z-auto —
  // 세션 결제 CTA/장바구니 푸터)를 덮는다. #636 정책상 도크 z 상향은 금지(모달 역전)이므로,
  // #634 와 동일한 "상호배제 렌더"로 해소: 메뉴 페이지 하단은 도크가 점유한다.
  // 상태 기반 메뉴 렌더(showPickupMenu=true)에서는 원래 TabNav 가 아닌 PickupCartNav 가
  // 렌더되므로, 이 분기는 route/상태 두 진입 경로의 하단 네비 동작을 정합시킨다
  // (메뉴 페이지에서 TabNav 가 보이는 경로 자체가 두 진입 방식 간 불일치였음).
  if (location.pathname.startsWith('/restaurant/')) {
    return null;
  }

  // Default navigation for other pages
  return (
    <TabNav
      activeTab={activeTab}
      onTabChange={onTabChange}
    />
  );
}

export type { BottomNavigationProps } from './types';
